import { Button, Checkbox, Infobox } from '@zoom/components';
import clsx from 'clsx';
import { startOfDay, addSeconds, set, getMinutes, getHours } from 'date-fns';
import { equals } from 'ramda';
import React, { Dispatch, useMemo } from 'react';
import { FieldMetaState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Label } from 'reactstrap';

import { useAuth } from '../../../app/AuthorizationProvider';
import { ComposedInfobox } from '../../../components/ComposedInfobox/ComposedInfobox';
import { DatePicker } from '../../../components/DatePicker/DatePicker';
import { TimePicker } from '../../../components/DatePicker/TimePicker';
import { Legend } from '../../../components/Form/Legend';
import { SidebarBody, SidebarHeader, Sidebar } from '../../../components/layout/Sidebar';
import { ForecastType } from '../../../graphql/components';
import {
  ONE_HOUR_IN_SECONDS,
  ONE_MINUTE_IN_SECONDS,
  convertTimeFromNumberToStringValue,
  convertTimeFromStringToNumberValue,
  identifyTimeFormat,
} from '../../../utils/time';
import { initialSchedulingState } from '../config';
import { getRangePropTag, RESET_ALL_CONFIGURATIONS_BUTTON_TAG } from '../constants';
import { AutoScheduleOptions, AutoScheduleSelectedRange } from '../types';
import { PrioritizeSelector } from './PrioritizeSelector';
import {
  Action,
  resetAutoScheduleOptionsAndSelectedRange,
  setAutoScheduleOptions,
  setAutoScheduleSelectedRange,
} from './actions';

type ScheduleConfigurationSidebarProps = {
  autoScheduleOptions: AutoScheduleOptions;
  autoScheduleSelectedRange: AutoScheduleSelectedRange;
  dispatch: Dispatch<Action>;
  forecastType: ForecastType;
  handleCancel: () => void;
  maxDate: Date;
  minDate: Date;
};

export const getTimeValue = (timestamp = 0) => {
  const time = getHours(timestamp) * ONE_HOUR_IN_SECONDS + getMinutes(timestamp) * ONE_MINUTE_IN_SECONDS;

  return convertTimeFromNumberToStringValue(time);
};

export const getDateTime = (timeValue: string, currentValue = 0, returnAsDate = false) => {
  const newValueInSeconds = timeValue === '' ? 0 : convertTimeFromStringToNumberValue(timeValue);

  const date = set(currentValue, { hours: 0, minutes: 0 });
  const datetime = addSeconds(date, newValueInSeconds);

  return returnAsDate ? datetime : datetime.getTime();
};

const validateTime = (a = 0, b = 0): FieldMetaState<any> => {
  return a >= b
    ? {
        // To satisfy TimePicker conditions
        valid: false,
        modified: true,

        // To satisfy DatePicker conditions
        invalid: true,
        touched: true,
      }
    : {};
};

export const ScheduleConfigurationSidebar: React.FC<ScheduleConfigurationSidebarProps> = ({
  autoScheduleOptions,
  autoScheduleSelectedRange,
  dispatch,
  forecastType,
  handleCancel,
  maxDate,
  minDate,
}) => {
  const { t } = useTranslation();
  const { locale } = useAuth();

  const { autoScheduleOptions: initialAutoSchedulingOptions } = initialSchedulingState;

  const maxDateNumber = maxDate.getTime();
  const minDateNumber = minDate.getTime();

  const maxDateEndRangeValueForSelection = startOfDay(maxDateNumber).getTime();

  const isResetDisabled = useMemo(
    () =>
      autoScheduleSelectedRange.startDateTime === minDateNumber &&
      autoScheduleSelectedRange.endDateTime === maxDateEndRangeValueForSelection &&
      equals(autoScheduleOptions, initialAutoSchedulingOptions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autoScheduleOptions, autoScheduleSelectedRange]
  );

  const timeFormat = identifyTimeFormat(locale);
  const meta = validateTime(autoScheduleSelectedRange.startDateTime, autoScheduleSelectedRange.endDateTime);
  const strictSelectedRangeCheckboxDisabled = forecastType === ForecastType.Intraday;

  return (
    <Sidebar>
      <SidebarHeader onClose={handleCancel}>
        <Button
          className="button-icon-left ml-auto"
          data-test-id={RESET_ALL_CONFIGURATIONS_BUTTON_TAG}
          disabled={isResetDisabled}
          kind="link"
          onClick={() =>
            dispatch(resetAutoScheduleOptionsAndSelectedRange(maxDateEndRangeValueForSelection, minDateNumber))
          }
        >
          {t('form.resetChanges')}
        </Button>
      </SidebarHeader>
      <SidebarBody>
        <div className="p-4">
          <Legend>{t('schedule.autoSchedule.scheduleConfiguration')}</Legend>
          <Label className="configuration-sidebar__section-label">{t('date.dateRange')}</Label>
          <div className="row">
            <div className="col-6">
              <DatePicker
                inputId="startDateTime"
                input={{
                  name: 'startDateTime',
                  onChange: (value) => {
                    dispatch(setAutoScheduleSelectedRange('startDateTime', value ? value.getTime() : minDateNumber));
                  },
                  value: new Date(autoScheduleSelectedRange.startDateTime),
                  onBlur: () => null,
                  onFocus: () => null,
                }}
                meta={meta}
                maxDate={maxDate}
                minDate={minDate}
                dataTestId={getRangePropTag('startDateTime')}
                endDate={new Date(autoScheduleSelectedRange.endDateTime)}
                label={t('date.dateFrom')}
                noValidation={!meta.invalid}
                selectsStart
                startDate={new Date(autoScheduleSelectedRange.startDateTime)}
              />
            </div>
            <div className="col-6">
              <TimePicker
                data-test-id={getRangePropTag('startTime')}
                inputName="startTime"
                label={t('shiftTemplate.startTime')}
                meta={meta}
                onSelect={(timeValue: string) => {
                  const datetime = getDateTime(timeValue, autoScheduleSelectedRange.startDateTime);
                  dispatch(setAutoScheduleSelectedRange('startDateTime', datetime as number));
                }}
                timeFormat={timeFormat}
                value={getTimeValue(autoScheduleSelectedRange.startDateTime ?? 0)}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-6">
              <DatePicker
                inputId="endDateTime"
                input={{
                  name: 'endDateTime',
                  onChange: (value) => {
                    dispatch(
                      setAutoScheduleSelectedRange(
                        'endDateTime',
                        value ? value.getTime() : maxDateEndRangeValueForSelection
                      )
                    );
                  },
                  value: new Date(autoScheduleSelectedRange.endDateTime),
                  onBlur: () => null,
                  onFocus: () => null,
                }}
                meta={meta}
                maxDate={maxDate}
                minDate={minDate}
                dataTestId={getRangePropTag('endDateTime')}
                endDate={new Date(autoScheduleSelectedRange.endDateTime)}
                label={t('date.dateTo')}
                noValidation={!meta.invalid}
                selectsEnd
                startDate={new Date(autoScheduleSelectedRange.startDateTime)}
              />
            </div>
            <div className="col-6">
              <TimePicker
                data-test-id={getRangePropTag('endTime')}
                inputName="endTime"
                label={t('shiftTemplate.endTime')}
                meta={meta}
                onSelect={(timeValue: string) => {
                  const datetime = getDateTime(timeValue, autoScheduleSelectedRange.endDateTime);
                  dispatch(setAutoScheduleSelectedRange('endDateTime', datetime as number));
                }}
                timeFormat={timeFormat}
                value={getTimeValue(autoScheduleSelectedRange.endDateTime ?? 0)}
              />
            </div>
          </div>
          <div className="mb-2 row strict-selected-range__checkbox-container">
            <Checkbox
              class={clsx('checkbox__checkbox', {
                'checkbox__checkbox--disabled': strictSelectedRangeCheckboxDisabled,
              })}
              data-test-id={getRangePropTag('strictSelectedRange')}
              disabled={strictSelectedRangeCheckboxDisabled}
              checked={autoScheduleSelectedRange.strictSelectedRange}
              onClick={() => {
                dispatch(
                  setAutoScheduleSelectedRange('strictSelectedRange', !autoScheduleSelectedRange.strictSelectedRange)
                );
              }}
            >
              <span
                className={clsx('checkbox-label', {
                  'checkbox-label--disabled text-grey-500': strictSelectedRangeCheckboxDisabled,
                })}
              >
                {t('schedule.autoSchedule.optimizeActivitiesWithinSelectedRange')}
              </span>
            </Checkbox>
            <Infobox>
              {t(
                strictSelectedRangeCheckboxDisabled
                  ? 'schedule.autoSchedule.optimizeActivitiesInfoboxMessageForIntradayForecast'
                  : 'schedule.autoSchedule.optimizeActivitiesInfoboxMessage'
              )}
            </Infobox>
          </div>

          {meta.invalid && (
            <ComposedInfobox mainMessageType="error">
              {t('response.fe.autoscheduler.schedule.selectedRangeInvalid')}
            </ComposedInfobox>
          )}

          <div className="configuration-sidebar__optimization-wrapper">
            <Label className="configuration-sidebar__section-label">
              {t('schedule.autoSchedule.schedulePriorities')}
            </Label>
            {Object.keys(autoScheduleOptions).map((optionKey) => (
              <PrioritizeSelector
                hintMessage={t(`schedule.autoSchedule.optimization.${optionKey}.hint`)}
                key={optionKey}
                id={optionKey as keyof AutoScheduleOptions}
                label={t(`schedule.autoSchedule.optimization.${optionKey}.label`)}
                setValue={(value: number) =>
                  dispatch(setAutoScheduleOptions(optionKey as keyof AutoScheduleOptions, value))
                }
                t={t}
                value={autoScheduleOptions[optionKey as keyof AutoScheduleOptions]}
              />
            ))}
          </div>
        </div>
      </SidebarBody>
    </Sidebar>
  );
};
